import { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import CartContext from '../../context/CartContext';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { addToCart } = useContext(CartContext);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch featured products
        const productsRes = await axios.get('/api/products');
        
        // Filter featured products
        const featured = productsRes.data.filter(product => product.featured);
        setFeaturedProducts(featured.slice(0, 4)); // Show max 4 featured products
        
        // Extract unique categories
        const uniqueCategories = [...new Set(productsRes.data.map(product => product.category))];
        setCategories(uniqueCategories.slice(0, 6)); // Show max 6 categories
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load products');
        setLoading(false);
        console.error(err);
      }
    };
    
    fetchData();
  }, []);
  
  const handleAddToCart = (product) => {
    addToCart(product);
    // Show toast or notification
    alert(`${product.name} added to cart!`);
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="neumorph p-8 rounded-full">
          <svg className="animate-spin h-8 w-8 text-gray-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-8">
        <div className="neumorph p-6 rounded-lg inline-block">
          <h3 className="text-xl text-red-600 mb-2">Error</h3>
          <p className="text-gray-700">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="neumorph-btn mt-4 px-4 py-2"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      {/* Hero Section */}
      <section className="neumorph rounded-lg p-8 mb-12">
        <div className="flex flex-col md:flex-row items-center">
          <div className="md:w-1/2 mb-8 md:mb-0">
            <h1 className="text-4xl font-bold mb-4">Welcome to EcomSandbox</h1>
            <p className="text-xl text-gray-700 mb-6">
              A lightweight e-commerce platform for testing and integration purposes.
            </p>
            <div className="flex space-x-4">
              <Link to="/products" className="neumorph-btn px-6 py-3 text-lg">
                Browse Products
              </Link>
              <Link to="/register" className="neumorph-btn px-6 py-3 text-lg">
                Get Started
              </Link>
            </div>
          </div>
          <div className="md:w-1/2">
            <div className="neumorph p-4 rounded-lg">
              <img 
                src="https://images.unsplash.com/photo-1607082350899-7e105aa886ae?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" 
                alt="E-commerce" 
                className="rounded-lg w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Featured Products */}
      <section className="mb-12">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Featured Products</h2>
          <Link to="/products" className="text-blue-600 hover:underline">
            View All
          </Link>
        </div>
        
        {featuredProducts.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600">No featured products available.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredProducts.map(product => (
              <div key={product._id} className="neumorph rounded-lg overflow-hidden">
                <Link to={`/products/${product._id}`}>
                  <img 
                    src={product.imageUrl} 
                    alt={product.name} 
                    className="w-full h-48 object-cover"
                  />
                </Link>
                <div className="p-4">
                  <Link to={`/products/${product._id}`}>
                    <h3 className="text-lg font-semibold mb-2">{product.name}</h3>
                  </Link>
                  <p className="text-gray-600 mb-2">${product.price.toFixed(2)}</p>
                  <button 
                    onClick={() => handleAddToCart(product)}
                    className="neumorph-btn w-full py-2"
                    disabled={product.stock <= 0}
                  >
                    {product.stock > 0 ? 'Add to Cart' : 'Out of Stock'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      
      {/* Categories */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Shop by Category</h2>
        
        {categories.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600">No categories available.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map(category => (
              <Link 
                key={category} 
                to={`/products?category=${category}`}
                className="neumorph p-6 rounded-lg text-center hover:shadow-neumorph-inset transition-all duration-300"
              >
                <h3 className="text-xl font-semibold">{category}</h3>
              </Link>
            ))}
          </div>
        )}
      </section>
      
      {/* Features */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold mb-6">Why Choose Us</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="neumorph p-6 rounded-lg">
            <div className="flex items-center justify-center mb-4">
              <svg className="h-12 w-12 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2 text-center">Secure Authentication</h3>
            <p className="text-gray-600 text-center">
              Multiple authentication options including Google OAuth and phone verification.
            </p>
          </div>
          
          <div className="neumorph p-6 rounded-lg">
            <div className="flex items-center justify-center mb-4">
              <svg className="h-12 w-12 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2 text-center">Flexible Payments</h3>
            <p className="text-gray-600 text-center">
              Integration with PayHere and 2Checkout payment gateways.
            </p>
          </div>
          
          <div className="neumorph p-6 rounded-lg">
            <div className="flex items-center justify-center mb-4">
              <svg className="h-12 w-12 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2 text-center">Admin Dashboard</h3>
            <p className="text-gray-600 text-center">
              Powerful admin interface to manage products, orders, and users.
            </p>
          </div>
        </div>
      </section>
      
      {/* Call to Action */}
      <section className="neumorph rounded-lg p-8 text-center">
        <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
        <p className="text-xl text-gray-700 mb-6 max-w-2xl mx-auto">
          Join our platform today and experience the seamless integration of authentication, payments, and e-commerce functionality.
        </p>
        <div className="flex justify-center space-x-4">
          <Link to="/register" className="neumorph-btn px-6 py-3 text-lg">
            Create Account
          </Link>
          <Link to="/products" className="neumorph-btn px-6 py-3 text-lg">
            Browse Products
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
